import dayjs from 'dayjs';

import {
    CommonEventStatusEnum,
    EventAgeEnum,
    EventDetailType,
    EventFormType,
    EventRequestType,
    EventTypeEnum,
} from '../../../service/events/events-type';
import {ProvidersIdsEnum} from '../../../util/type';
import {PublicationCompaniesSourceEnum} from '../../publication-wizard/publication-brand-or-companies-field/publication-brand-or-companies-helper';

// The design system's standard form-modal width, which is what the 520 in Figma rounds to.
export const EVENT_FORM_MODAL_WIDTH = 530;

// Catalogs are hidden from the UI for now — events always target Yandex.
const EVENT_CATALOG_IDS = [ProvidersIdsEnum.yandex];

type EventFormFilledStateType = {
    hasBrand: boolean;
    hasCompanies: boolean;
    hasDescription: boolean;
    hasEventType: boolean;
    hasImages: boolean;
    hasPeriod: boolean;
    hasTitle: boolean;
};

export function getEventFormInitialValues(event: EventDetailType): EventFormType {
    return {
        title: event.title,
        description: event.description,
        eventType: event.eventType,
        minAge: event.minAge,
        images: event.images,
        period: [event.startAt ? dayjs(event.startAt) : null, event.finishAt ? dayjs(event.finishAt) : null],
        // Not editable any more, but carried through the form: the update endpoint assigns both
        // fields straight from the payload, so leaving them out would wipe the dates of events
        // created before the publication period was dropped from the UI.
        publishAt: event.publishAt ? dayjs(event.publishAt) : null,
        deletedAt: event.deletedAt ? dayjs(event.deletedAt) : null,
    };
}

// Only the parts of the form the submit button depends on. Watched instead of the whole form, so
// typing in the description (up to 3000 characters) does not re-render the modal on every keystroke.
export function selectEventFormFilledState(values: EventFormType | undefined): EventFormFilledStateType {
    return {
        hasBrand: Boolean(values?.brandId),
        hasCompanies: (values?.companiesCount ?? 0) > 0,
        hasDescription: Boolean(values?.description?.trim()),
        hasEventType: Boolean(values?.eventType),
        hasImages: (values?.images ?? []).length > 0,
        // Only whether the period has been touched at all. Both of its ends are checked by the field's
        // own rule, so a half-filled range has to reach validation instead of silently disabling submit.
        hasPeriod: Boolean(values?.period),
        hasTitle: Boolean(values?.title?.trim()),
    };
}

// Drives the submit button: every required field has to be filled before the event can be sent.
// Kept explicit rather than derived from `getFieldsError()`, which only reports fields that have
// already been validated — untouched empty required fields would read as valid there.
//
// The state is optional because `Form.useWatch` runs its selector in an effect, not while rendering,
// and returns `undefined` until then — its types do not say so. Nothing is known about the form on
// that first render, which is exactly when it is still empty, so it counts as not filled.
export function isEventFormFilled(
    state: EventFormFilledStateType | undefined,
    companiesSource: PublicationCompaniesSourceEnum
): boolean {
    if (!state) {
        return false;
    }

    const isSelectionFilled =
        companiesSource === PublicationCompaniesSourceEnum.Brand ? state.hasBrand : state.hasCompanies;

    return (
        isSelectionFilled &&
        state.hasTitle &&
        state.hasDescription &&
        state.hasEventType &&
        state.hasPeriod &&
        state.hasImages
    );
}

export function prepareEventRequestData(
    values: EventFormType,
    selectorId: string,
    status: CommonEventStatusEnum
): EventRequestType {
    const [startAt, finishAt] = values.period ?? [null, null];

    return {
        title: values.title,
        description: values.description ?? '',
        eventType: values.eventType ?? EventTypeEnum.Occasion,
        minAge: values.minAge ?? EventAgeEnum.Age0,
        selectorId,
        catalogsIds: EVENT_CATALOG_IDS,
        // Not part of the form any more; an existing event keeps whatever it was created with.
        publishAt: values.publishAt ? values.publishAt.toISOString() : null,
        deletedAt: values.deletedAt ? values.deletedAt.toISOString() : null,
        startAt: startAt ? startAt.toISOString() : '',
        finishAt: finishAt ? finishAt.toISOString() : '',
        imageIds: (values.images ?? []).map((image) => image.id),
        status,
    };
}
